<template>
  <div>
    <v-dialog v-model="reminder" max-width="460" :persistent="blocked">
      <v-card v-if="current">
        <v-card-title>{{ current.stage === 'expired' ? '校区已过期' : '校区续费提醒' }}</v-card-title>
        <v-card-text>
          <p class="text-subtitle-1">{{ current.shopName }}</p>
          <p>{{ reminderText }}，有效期至 {{ current.expireDate }}。</p>
          <p>续费保持当前版本，优惠价 ¥1,000 / 校区 / 年。</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dismissReminder">{{ blocked ? '查看续费页面' : '稍后提醒' }}</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="dismissReminder(); open()">立即续费</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card v-if="!ready || blocked" class="ma-6 pa-6" outlined>
      <v-card-title>{{ !ready ? '正在确认校区有效期' : '当前校区已过期' }}</v-card-title>
      <v-card-text>{{ error || '续费后即可继续使用，原有数据和版本权益保留。您也可以切换至其他有效校区。' }}</v-card-text>
      <v-card-actions>
        <v-btn v-if="ready" color="primary" @click="open()">校区续费</v-btn>
        <v-btn text :loading="loading" @click="refresh">刷新状态</v-btn>
        <v-btn text @click="$emit('logout')">退出登录</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialog" max-width="650" scrollable content-class="renewal-overview">
      <v-card>
        <v-card-title>校区续费<v-spacer /><v-btn icon aria-label="关闭" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text class="pt-3">
          <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
          <v-select v-model="selectedID" :items="shops" item-text="shopName" item-value="id" label="续费校区" outlined dense :disabled="saving" @change="clearOrder" />
          <template v-if="selected">
            <v-sheet rounded color="blue lighten-5" class="pa-5 mb-4">
              <div class="text-subtitle-1">{{ selected.shopName }} · {{ versionName(selected.vipLevel) }}</div>
              <div class="mt-2">当前有效期至：<strong class="text-h6">{{ selected.expireDate }}</strong></div>
              <div v-if="selected.daysRemaining < 0" class="error--text mt-2">校区已过期，续费后可继续使用。</div>
            </v-sheet>
            <v-alert v-if="selected.issue" type="warning" text>{{ selected.issue }}</v-alert>
            <div class="d-flex align-center mb-3"><strong>待支付续费单</strong><v-spacer /><v-btn small text :loading="historyLoading" @click="loadOrders">刷新订单</v-btn></div>
            <v-progress-linear v-if="historyLoading && !historyLoaded" indeterminate class="mb-4" />
            <v-alert v-if="historyError" type="error" text>{{ historyError }}，请刷新订单后重试。</v-alert>
            <template v-if="historyLoaded && !historyError">
              <v-sheet v-for="item in pendingOrders" :key="item.orderNo" outlined rounded class="pa-4 mb-3">
                <div class="d-flex align-center"><strong>{{ stateName(item) }}</strong><v-spacer /><span class="text-h6 primary--text">¥{{ money(item.amountCents) }}</span></div>
                <div class="text-caption mt-2">订单号：{{ item.orderNo }}</div>
                <div class="mt-2">{{ versionName(item.vipLevel) }} · 续费 1 年 · 微信支付</div>
                <v-btn color="primary" class="mt-3" :loading="saving" :disabled="!paymentEnabled || !ready || !!selected.issue" @click="openOrder(item)">继续支付</v-btn>
              </v-sheet>
              <v-alert v-if="reviewOrders.length" type="warning" text>有续费订单待客服核实，请先联系客服确认付款结果，暂勿重复付款。</v-alert>
              <v-sheet v-if="!pendingOrders.length && !reviewOrders.length" outlined rounded class="pa-5 mb-4">
                <p class="grey--text">当前校区暂无待支付的续费单</p>
                <div class="text-subtitle-1 font-weight-bold">当前版本 · 续费 1 年</div>
                <div class="my-3"><span class="text-h5 primary--text">¥{{ money(amountCents) }}</span> / 校区 / 年 <del class="ml-3 grey--text">原价 ¥{{ money(originalCents) }}</del></div>
                <v-btn color="primary" :loading="saving" :disabled="!!selected.issue || !ready || historyLoading" @click="createOrder">我要续费</v-btn>
              </v-sheet>
            </template>
            <v-alert v-if="!paymentEnabled" type="info" text>在线支付暂未开放。您可以先保存续费订单，或联系客服办理续费。保存订单不会扣款或延长有效期。</v-alert>
            <v-expansion-panels v-if="otherOrders.length" flat class="mt-4">
              <v-expansion-panel><v-expansion-panel-header>历史续费订单（{{ otherOrders.length }}）</v-expansion-panel-header><v-expansion-panel-content>
                <v-list dense>
                  <v-list-item v-for="item in otherOrders" :key="item.orderNo">
                    <v-list-item-content>
                      <v-list-item-title>¥{{ money(item.amountCents) }} · {{ stateName(item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.orderNo }}</v-list-item-subtitle>
                      <v-list-item-subtitle v-if="item.state === 'paid'">已续费至 {{ item.renewTo }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action><v-btn small text :disabled="saving" @click="openOrder(item)">查看</v-btn></v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content></v-expansion-panel>
            </v-expansion-panels>
            <p class="mt-4 mb-0">客服微信：15674980303</p>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="paymentDialog" max-width="540" scrollable :persistent="saving" content-class="renewal-payment">
      <v-card>
        <v-card-title>校区续费付款<v-spacer /><v-btn icon aria-label="关闭付款" :disabled="saving" @click="paymentDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text class="pt-3">
          <v-alert v-if="paymentError" type="error" text>{{ paymentError }}</v-alert>
          <div v-if="saving && !order" class="text-center pa-6"><v-progress-circular indeterminate color="primary" /><p class="mt-4">正在生成续费订单，请稍候…</p></div>
          <template v-if="order">
            <v-sheet rounded color="blue lighten-5" class="pa-4 mb-4">
              <div>{{ order.shopName }} · {{ versionName(order.vipLevel) }}</div>
              <div class="text-subtitle-1 font-weight-bold">当前版本 · 续费 1 年</div>
              <div class="my-3"><span class="text-h4 primary--text">¥{{ money(order ? order.amountCents : amountCents) }}</span> / 校区 / 年 <del class="ml-3 grey--text">原价 ¥{{ money(originalCents) }}</del></div>
              <div>{{ order.state === 'paid' ? '本次续费后有效期至' : '预计续费后有效期至' }}：<strong>{{ order.state === 'paid' ? order.renewTo : (selected && selected.renewTo || '待核实') }}</strong></div>
              <div class="text-caption mt-2">未过期从原到期日顺延；已过期从付款当天开始。以付款时的校区有效期为准。</div>
            </v-sheet>
            <v-sheet v-if="order" outlined rounded class="mt-4 pa-4" aria-live="polite">
              <div class="font-weight-bold">{{ order.shopName }} · {{ stateName(order) }}</div>
              <div class="text-caption mt-1">订单号：{{ order.orderNo }}</div>
              <div class="mt-2">应付 ¥{{ money(order.amountCents) }}，保持 {{ versionName(order.vipLevel) }}。</div>
              <div v-if="order.state === 'pending' && order.qrImage && !orderExpired" class="text-center mt-3">
                <img :src="order.qrImage" alt="微信支付二维码" width="240" height="240" />
                <p class="mb-1">请使用微信扫一扫支付</p>
                <p class="text-caption">二维码剩余 {{ countdown }}，付款后将自动续费 1 年。</p>
              </div>
              <v-alert v-if="order.state === 'creating'" type="info" text class="mt-3">正在确认支付订单，请稍候或刷新支付状态。重复操作会继续使用同一订单。</v-alert>
              <v-alert v-if="order.state === 'draft'" type="info" text class="mt-3">订单已保存，尚未付款。</v-alert>
              <v-alert v-if="order.state === 'repriced'" type="info" text class="mt-3">订单价格已调整，原二维码已失效，正在读取新付款订单。</v-alert>
              <v-alert v-if="order.state === 'paid'" type="success" text class="mt-3">支付成功，已续费至 {{ order.renewTo }}。</v-alert>
              <v-alert v-if="order.state === 'review'" type="warning" text class="mt-3">支付信息需要客服核实，请提供订单号，暂勿重复付款。</v-alert>
              <v-alert v-if="order.state === 'closed' || (orderExpired && ['draft', 'creating', 'pending'].includes(order.state))" type="info" text class="mt-3">二维码已到期。若已付款，请先刷新支付状态；未付款可返回校区续费重新下单。</v-alert>
              <v-btn v-if="['creating', 'pending', 'closed', 'repriced'].includes(order.state)" text :loading="polling" @click="pollOrder">刷新支付状态</v-btn>
              <v-btn v-if="order.state === 'paid'" color="primary" text @click="finishPayment">完成并刷新页面</v-btn>
            </v-sheet>
            <p class="mt-4 mb-0">客服微信：15674980303</p>
          </template>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text :disabled="saving" @click="paymentDialog = false">返回校区续费</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import global from '../config/global'
import storage from '../config/storage'
import { EventBus } from '../config/eventBus'
export default {
  name: 'CampusRenewal',
  props: { shopId: { default: '' }, employeeId: { default: '' } },
  data: () => ({ shops: [], ready: false, loading: false, error: '', dialog: false, reminder: false, paymentDialog: false, paymentError: '',
    selectedID: null, channel: 'wechat', order: null, orders: [], saving: false,
    historyLoading: false, historyLoaded: false, historyGeneration: 0, historyError: '', amountCents: 100000, originalCents: 128000,
    channels: [], polling: false, pollTimer: null, clockTimer: null, clock: Date.now(), paymentGeneration: 0, completedOrder: null,
    dismissed: {}, timer: null, disposed: false }),
  computed: {
    selectedOrders() { return this.orders.filter(o => String(o.shopId) === String(this.selectedID)) },
    pendingOrders() { return this.selectedOrders.filter(o => ['draft', 'creating', 'pending'].includes(o.state) && new Date(o.expiresAt).getTime() > this.clock) },
    reviewOrders() { return this.selectedOrders.filter(o => o.state === 'review') },
    otherOrders() { return this.selectedOrders.filter(o => !this.pendingOrders.some(p => p.orderNo === o.orderNo)) },
    paymentEnabled() { return this.channels.some(c => c.code === this.channel && c.enabled) },
    orderExpired() { return this.order && new Date(this.order.expiresAt).getTime() <= this.clock },
    countdown() { const seconds = Math.max(0, Math.ceil((new Date(this.order.expiresAt).getTime() - this.clock) / 1000)); return `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒` },
    current() { return this.shopId ? this.shops.find(s => String(s.id) === String(this.shopId)) : this.shops[0] },
    selected() { return this.shops.find(s => s.id === this.selectedID) },
    blocked() { return !this.current || this.current.status !== '1' || this.current.daysRemaining < 0 || !this.current.renewTo },
    reminderText() {
      if (!this.current) return ''
      if (this.current.daysRemaining < 0) return '该校区已过期'
      return this.current.daysRemaining === 0 ? '今天是有效期最后一天' : `距离到期还有 ${this.current.daysRemaining} 天`
    }
  },
  watch: {
    shopId() { this.updateAccess(); this.showReminder() },
    employeeId() { this.showReminder() },
    dialog(value) { if (value) this.loadOrders(); else { this.paymentDialog = false; this.stopPolling() } },
    paymentDialog(value) { if (!value) { this.stopPolling(); if (this.dialog) this.loadOrders() } }
  },
  mounted() {
    this.refresh()
    this.timer = setInterval(this.refresh, 5 * 60 * 1000)
    this.clockTimer = setInterval(() => { this.clock = Date.now() }, 1000)
    document.addEventListener('visibilitychange', this.onVisible)
  },
  beforeDestroy() {
    this.disposed = true
    clearInterval(this.timer)
    clearInterval(this.clockTimer)
    this.stopPolling()
    document.removeEventListener('visibilitychange', this.onVisible)
  },
  methods: {
    money(cents) { return (cents / 100).toLocaleString('zh-CN') },
    versionName(level) { return level ? `${level} 版` : '待核实' },
    stateName(order) {
      if (order.state === 'repriced') return '已改价，原二维码失效'
      if (order.state === 'paid') return '已付款并续费'
      if (order.state === 'review') return '待客服核实'
      if (order.state === 'creating') return '确认支付订单中'
      if (order.state === 'closed' || new Date(order.expiresAt).getTime() < Date.now()) return '已失效'
      return order.state === 'draft' ? '已保存，未付款' : '待付款'
    },
    onVisible() { if (!document.hidden) this.refresh() },
    updateAccess() { this.$emit('access', { ready: this.ready, blocked: this.blocked, current: this.current }) },
    async refresh() {
      if (this.loading || this.disposed) return
      this.loading = true
      try {
        const { data } = await this.axios.get('/renewal/status')
        if (data.code !== 200) throw new Error(data.message || '读取续费信息失败')
        if (this.disposed) return
        this.shops = data.data.shops
        this.amountCents = data.data.amountCents
        this.originalCents = data.data.originalCents
        this.channels = data.data.channels || []
        this.ready = true
        this.error = ''
        if (!this.selected) this.selectedID = this.current && this.current.id
        this.updateAccess()
        this.showReminder()
      } catch (e) {
        this.error = e && e.message || '无法连接服务，请稍后重试'
        this.ready = false
        this.updateAccess()
      } finally { this.loading = false }
    },
    reminderKey() {
      return this.current && `campus-renewal:${this.employeeId}:${this.current.id}:${this.current.expireDate}:${this.current.stage}`
    },
    showReminder() {
      if (!this.ready || !this.employeeId || !this.current || !this.current.stage) {
        this.reminder = false
        return
      }
      const key = this.reminderKey()
      let seen = this.dismissed[key]
      try { seen = seen || (this.current.stage !== 'expired' && localStorage.getItem(key)) } catch (_) { /* Storage may be disabled. */ }
      this.reminder = !seen && !this.dialog
    },
    dismissReminder() {
      const key = this.reminderKey()
      if (key) {
        this.dismissed[key] = true
        try { if (this.current.stage !== 'expired') localStorage.setItem(key, '1') } catch (_) { /* Keep session dismissal. */ }
      }
      this.reminder = false
    },
    open() {
      this.dismissReminder()
      this.selectedID = this.current && this.current.id
      this.clearOrder()
      this.historyLoaded = false
      this.historyError = ''
      this.dialog = true
      this.refresh()
    },
    async createOrder() {
      if (!this.selected || this.saving || !this.historyLoaded || this.historyLoading || this.historyError || this.pendingOrders.length || this.reviewOrders.length || !this.ready || this.selected.issue) return
      this.clearOrder()
      this.channel = 'wechat'
      this.paymentDialog = true
      this.saving = true
      this.paymentError = ''
      const generation = this.paymentGeneration
      const shopID = this.selected.id
      const channel = this.channel
      try {
        const { data } = await this.axios.post('/renewal/orders', { shopId: shopID, channel })
        if (data.code !== 200) throw new Error(data.message || '创建续费订单失败')
        if (generation === this.paymentGeneration && this.dialog && this.paymentDialog && this.selectedID === shopID && this.channel === channel) { this.order = data.data; await this.handlePaymentState() }
        await this.loadOrders()
      } catch (e) { if (generation === this.paymentGeneration) this.paymentError = e && e.message || '保存失败，请稍后重试' }
      finally { this.saving = false }
    },
    stopPolling() { clearTimeout(this.pollTimer); this.pollTimer = null; this.paymentGeneration++ },
    clearOrder() { this.paymentDialog = false; this.stopPolling(); this.order = null; this.paymentError = ''; this.completedOrder = null },
    schedulePoll() {
      clearTimeout(this.pollTimer)
      if (!this.disposed && this.dialog && this.paymentDialog && this.order && ['creating', 'pending', 'repriced'].includes(this.order.state)) this.pollTimer = setTimeout(this.pollOrder, 5000)
    },
    async pollOrder() {
      if (!this.order || this.polling || !this.dialog || !this.paymentDialog) return
      clearTimeout(this.pollTimer)
      this.polling = true
      const number = this.order.orderNo
      const generation = this.paymentGeneration
      try {
        const { data } = await this.axios.get('/renewal/orders/' + encodeURIComponent(number))
        if (data.code !== 200) throw new Error(data.message || '查询支付状态失败')
        if (generation !== this.paymentGeneration || !this.order || this.order.orderNo !== number || !this.dialog || !this.paymentDialog) return
        this.order = data.data
        this.paymentError = ''
        await this.handlePaymentState()
      } catch (e) { if (generation === this.paymentGeneration) this.paymentError = e && e.message || '暂时无法确认支付结果，请稍后刷新，勿重复付款' }
      finally { this.polling = false; this.schedulePoll() }
    },
    async handlePaymentState() {
      if (!this.order || !this.paymentDialog) return
      if (this.order.state === 'repriced' && this.order.replacedBy) {
        const oldNumber = this.order.orderNo
        const generation = this.paymentGeneration
        const { data } = await this.axios.post('/renewal/orders/' + encodeURIComponent(this.order.replacedBy) + '/pay', {})
        if (generation !== this.paymentGeneration || !this.dialog || !this.paymentDialog || !this.order || this.order.orderNo !== oldNumber) return
        if (data.code !== 200) throw new Error(data.message || '读取改价后的订单失败，请刷新重试')
        this.order = data.data
        await this.loadOrders()
      }
      if (this.order.state === 'paid' && this.completedOrder !== this.order.orderNo) {
        const number = this.order.orderNo
        const generation = this.paymentGeneration
        try {
          // Refresh staff/campus permissions before remounting expired business views.
          const { data } = await this.axios.get('/sys/employee/myself/info')
          if (this.disposed || generation !== this.paymentGeneration || !this.order || this.order.orderNo !== number) return
          if (data.code !== 200) throw new Error('profile refresh failed')
          global.employee = data.data.employee
          global.routeTree = data.data.menuTree
          const current = storage.getCurrentShop()
          const renewed = (global.employee.shopList || []).find(s => current && s.id === current.id)
          if (renewed) { storage.setCurrentShop(renewed); EventBus.$emit('updateShop') }
          this.completedOrder = number
          await this.refresh()
          await this.loadOrders()
        } catch (e) {
          if (generation === this.paymentGeneration) this.paymentError = '续费已成功，请点击“完成并刷新页面”更新校区权限。'
        }
      }
      this.schedulePoll()
    },
    async openOrder(item) {
      if (this.saving) return
      this.clearOrder()
      this.selectedID = item.shopId
      this.channel = item.channel
      this.order = { ...item }
      this.paymentDialog = true
      this.saving = true
      const generation = this.paymentGeneration
      try {
        if (this.paymentEnabled && ['draft', 'creating', 'pending'].includes(item.state)) {
          const { data } = await this.axios.post('/renewal/orders/' + encodeURIComponent(item.orderNo) + '/pay', {})
          if (data.code !== 200) throw new Error(data.message || '无法继续支付')
          if (generation !== this.paymentGeneration || !this.dialog || !this.paymentDialog || !this.order || this.order.orderNo !== item.orderNo) return
          this.order = data.data
        }
        await this.handlePaymentState()
      } catch (e) { if (generation === this.paymentGeneration) this.paymentError = e && e.message || '暂时无法继续支付' }
      finally { this.saving = false }
    },
    finishPayment() { window.location.reload() },
    async loadOrders() {
      const generation = ++this.historyGeneration
      this.historyLoading = true
      this.historyError = ''
      try {
        const { data } = await this.axios.get('/renewal/orders')
        if (this.disposed || generation !== this.historyGeneration) return
        if (data.code !== 200) throw new Error(data.message || '读取订单失败')
        this.orders = data.data
        this.historyLoaded = true
      } catch (e) { if (generation === this.historyGeneration) { this.historyLoaded = false; this.historyError = e && e.message || '暂时无法读取续费订单' } }
      finally { if (generation === this.historyGeneration) this.historyLoading = false }
    }
  }
}
</script>
